import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';

import type { ManualVobFormInput } from '../schemas/manual-vob.schema';
import { formatDateOnlyLocal } from './dateHelpers';

const ELIGIBILITY_AUTOMATED_VERIFY_BOT: Identifier = {
  system: 'https://unify.health/bots',
  value: 'eligibility-automated-verify',
};

const ELIGIBILITY_MANUAL_ENTRY_BOT: Identifier = {
  system: 'https://unify.health/bots',
  value: 'eligibility-manual-entry',
};

function getAutomatedVerifyBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_ELIGIBILITY_AUTOMATED_VERIFY_BOT_ID?.trim();
  if (id) return id;
  return ELIGIBILITY_AUTOMATED_VERIFY_BOT;
}

function getManualEntryBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_ELIGIBILITY_MANUAL_ENTRY_BOT_ID?.trim();
  if (id) return id;
  return ELIGIBILITY_MANUAL_ENTRY_BOT;
}

export interface EligibilityManualEntryResult {
  ok: boolean;
  reason?: string;
  requestId?: string;
  responseId?: string;
}

export interface EligibilityAutomatedVerifyResult {
  ok: boolean;
  skipped?: boolean;
  reason?: string;
  verificationAttempts: number;
}

export async function retryEligibilityVerification(
  medplum: MedplumClient,
  coverageId: string,
): Promise<EligibilityAutomatedVerifyResult> {
  const coverage = await medplum.readResource('Coverage', coverageId);
  return medplum.executeBot(
    getAutomatedVerifyBotTarget(),
    coverage,
    ContentType.FHIR_JSON,
  ) as Promise<EligibilityAutomatedVerifyResult>;
}

export function toStaffManualVobPayload(
  values: ManualVobFormInput,
  options?: { serviceDate?: string },
): ManualVobFormInput {
  return {
    ...values,
    serviceDate: values.serviceDate ?? options?.serviceDate,
  };
}

/** Calendar date for manual VOB: tracker appointment in local time, otherwise today. */
export function manualVobServiceDateFromAppointment(
  appointmentInstant?: string,
  now: Date = new Date(),
): string {
  return formatDateOnlyLocal(appointmentInstant ? new Date(appointmentInstant) : now);
}

export async function submitManualVobEntry(
  medplum: MedplumClient,
  input: ManualVobFormInput,
): Promise<EligibilityManualEntryResult> {
  return medplum.executeBot(
    getManualEntryBotTarget(),
    toStaffManualVobPayload(input),
    ContentType.JSON,
  ) as Promise<EligibilityManualEntryResult>;
}
